<template>
  <div class="gs-cas" :class="{ 'is-open': open, 'is-disabled': disabled }">
    <div class="gs-cas-field" :class="{ 'is-placeholder': !text }">
      <input
        ref="search"
        v-model="text"
        class="gs-cas-input"
        type="text"
        :disabled="disabled"
        :placeholder="placeholder"
        autocomplete="off"
        @focus="show"
        @blur="onBlur"
        @keydown.esc.prevent="close"
        @keydown.enter.prevent="pickFirst"
      >
      <button
        v-if="clearable && (displayText || (editing && text)) && !disabled"
        type="button"
        class="gs-cas-clear"
        aria-label="清除"
        @click.stop="clear"
      >×</button>
      <span class="gs-cas-arrow" @click.stop="toggle" />
    </div>
    <div v-if="open" class="gs-cas-panel" :style="panelStyle" @mousedown.prevent @click.stop>
      <div v-if="editing && text.trim()" class="gs-cas-hits">
        <button
          v-for="item in filtered"
          :key="String(item.value)"
          type="button"
          class="gs-cas-hit"
          @click="pickPath(item.value)"
        >{{ item.mername || item.label }}</button>
        <div v-if="!filtered.length" class="gs-cas-empty">无匹配地区</div>
      </div>
      <div v-else class="gs-cas-cols">
        <div v-for="(col, level) in columns" :key="level" class="gs-cas-col">
          <button
            v-for="node in col"
            :key="String(node.value)"
            type="button"
            class="gs-cas-opt"
            :class="{ 'is-active': isActive(level, node) }"
            :title="node.label"
            @click="pickLevel(level, node)"
          >{{ node.label }}</button>
        </div>
      </div>
      <div v-if="requireLeaf && !complete" class="gs-cas-hint">请继续选择到区县一级</div>
    </div>
  </div>
</template>

<script>
import { displayOfPath, findNode, flattenOptions } from '@/utils/region'

export default {
  name: 'GsCascadeSelect',
  props: {
    value: {
      type: [Array, String],
      default: undefined
    },
    modelValue: {
      type: [Array, String],
      default: undefined
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: '请选择'
    },
    searchPlaceholder: {
      type: String,
      default: '搜索地区'
    },
    clearable: {
      type: Boolean,
      default: true
    },
    filterable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    /** 只允许选到末级（省 → 市 → 区县），未选到末级不向上返回值 */
    requireLeaf: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      open: false,
      text: '',
      open: false,
      focused: false,
      editing: false,
      localPath: null,
      box: { top: 0, left: 0, bottom: 0, width: 180 }
    }
  },
  computed: {
    current() {
      if (Array.isArray(this.localPath)) return this.localPath
      return this.boundPath()
    },
    displayText() {
      return displayOfPath(this.options, this.current)
    },
    columns() {
      const cols = [this.options || []]
      let list = this.options || []
      this.current.forEach((val) => {
        const hit = (list || []).find((item) => String(item.value) === String(val))
        if (!hit || !hit.children || !hit.children.length) return
        cols.push(hit.children)
        list = hit.children
      })
      return cols.filter((col) => col && col.length)
    },
    filtered() {
      if (!this.editing) return []
      const q = (this.text || '').trim().toLowerCase()
      if (!q) return []
      return flattenOptions(this.options).filter((item) => {
        return (item.label && item.label.toLowerCase().indexOf(q) !== -1)
          || (item.mername && String(item.mername).toLowerCase().indexOf(q) !== -1)
      }).slice(0, 40)
    },
    panelStyle() {
      // 省/市/区三列并排，面板至少 540px，否则每列只有 60px，文字全被截断
      const viewport = typeof window !== 'undefined' ? window.innerWidth : 1200
      const width = Math.min(Math.max(this.box.width || 0, 540), viewport - 16)
      let left = this.box.left || 0
      if (left + width > viewport - 8) left = Math.max(8, viewport - width - 8)
      return {
        top: (this.box.bottom + 4) + 'px',
        left: left + 'px',
        width: width + 'px'
      }
    },
    /** requireLeaf 模式下，当前选择是否已经落到区县 */
    complete() {
      return this.isLeafPath(this.current)
    }
  },
  watch: {
    value() {
      this.dropLocalIfSynced()
    },
    modelValue() {
      this.dropLocalIfSynced()
    },
    displayText: {
      immediate: true,
      handler(val) {
        if (this.focused && this.editing) return
        this.text = val || ''
      }
    },
    text(val) {
      if (!this.focused) return
      const shown = this.displayText || ''
      this.editing = (val || '') !== shown
      if (this.editing && !this.open) this.open = true
    }
  },
  mounted() {
    this._onDoc = (e) => {
      if (!this.$el.contains(e.target) && !this.panelContains(e.target)) this.close()
    }
    this._onWin = () => {
      if (this.open) this.syncBox()
    }
    document.addEventListener('mousedown', this._onDoc)
    window.addEventListener('resize', this._onWin)
    window.addEventListener('scroll', this._onWin, true)
  },
  beforeUnmount() {
    this.unbind()
  },
  beforeDestroy() {
    this.unbind()
  },
  methods: {
    unbind() {
      if (this._blurTimer) window.clearTimeout(this._blurTimer)
      if (this._onDoc) document.removeEventListener('mousedown', this._onDoc)
      if (this._onWin) {
        window.removeEventListener('resize', this._onWin)
        window.removeEventListener('scroll', this._onWin, true)
      }
    },
    panelContains(target) {
      const panel = this.$el.querySelector('.gs-cas-panel')
      return panel ? panel.contains(target) : false
    },
    syncBox() {
      const el = this.$el && this.$el.querySelector('.gs-cas-field')
      if (!el) return
      const rect = el.getBoundingClientRect()
      this.box = {
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        width: rect.width
      }
    },
    toggle() {
      if (this.disabled) return
      if (this.open) this.close()
      else this.show()
    },
    show() {
      if (this.disabled) return
      if (this._blurTimer) {
        window.clearTimeout(this._blurTimer)
        this._blurTimer = 0
      }
      this.focused = true
      this.editing = false
      if (!this.text) this.text = this.displayText || ''
      if (!this.open) {
        this.syncBox()
        this.open = true
      }
      this.$nextTick(() => {
        const input = this.$refs.search
        if (input && document.activeElement !== input) input.focus()
        else if (input && input.value) input.select()
      })
    },
    close() {
      this.open = false
      this.focused = false
      this.editing = false
      this.text = this.displayText || ''
    },
    onBlur() {
      const input = this.$refs.search
      this._blurTimer = window.setTimeout(() => {
        this._blurTimer = 0
        if (input && document.activeElement === input) return
        this.focused = false
        this.editing = false
        this.open = false
        this.text = this.displayText || ''
      }, 0)
    },
    pickFirst() {
      if ((this.text || '').trim() && this.filtered.length) this.pickPath(this.filtered[0].value)
    },
    isActive(level, node) {
      return String(this.current[level]) === String(node.value)
    },
    /** 已选中的节点是否已经没有下级（即选到了区县） */
    isLeafPath(path) {
      if (!path || !path.length) return false
      const node = findNode(this.options, path[path.length - 1])
      return !!node && !(node.children && node.children.length)
    },
    normalizePath(raw) {
      if (Array.isArray(raw)) return raw
      if (typeof raw === 'string' && raw.trim()) {
        try {
          const parsed = JSON.parse(raw)
          return Array.isArray(parsed) ? parsed : []
        } catch (e) {
          return []
        }
      }
      return []
    },
    boundPath() {
      // 注意：value 默认不能是 []，否则会盖掉 v-model 传入的已选地区
      const fromValue = this.normalizePath(this.value)
      const fromModel = this.normalizePath(this.modelValue)
      if (fromValue.length) return fromValue
      if (fromModel.length) return fromModel
      return fromValue
    },
    dropLocalIfSynced() {
      if (!Array.isArray(this.localPath)) return
      const prop = this.boundPath()
      if (prop.join('|') === this.localPath.join('|')) this.localPath = null
    },
    emitPath(path) {
      const next = Array.isArray(path) ? path.slice() : []
      this.localPath = next
      this.$emit('input', next)
      this.$emit('update:modelValue', next)
      this.$emit('change', next)
    },
    pickLevel(level, node) {
      const path = this.current.slice(0, level).concat(node.value)
      this.editing = false
      this.text = displayOfPath(this.options, path) || node.label
      const hasChildren = !!(node.children && node.children.length)
      if (this.requireLeaf && hasChildren) {
        // 还没选到区县：只更新本地高亮与显示，不向外发值
        this.localPath = path
        this.syncBox()
        return
      }
      this.emitPath(path)
      if (!hasChildren) this.close()
    },
    pickPath(path) {
      const next = path || []
      this.editing = false
      this.emitPath(next)
      this.text = displayOfPath(this.options, next)
      this.close()
    },
    clear() {
      this.editing = false
      this.text = ''
      this.emitPath([])
      this.close()
    }
  }
}
</script>

<style scoped>
.gs-cas {
  position: relative;
  width: 100%;
}
.gs-cas-field {
  display: flex;
  align-items: center;
  height: 36px;
  min-height: 36px;
  padding: 0 10px 0 12px;
  border: 1px solid #D7DEE8;
  border-radius: 4px;
  background: #fff;
  color: #1f2937;
  cursor: pointer;
  box-sizing: border-box;
}
.gs-cas-field:hover,
.gs-cas.is-open .gs-cas-field {
  border-color: #0165FF;
}
.gs-cas-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 32px;
  border: 0;
  outline: none;
  padding: 0;
  background: #fff;
  color: #111824;
  color-scheme: light;
  caret-color: #111824;
  font-size: 14px;
  appearance: none;
}
.gs-cas-input:focus {
  color: #111824;
  background: #fff;
  caret-color: #111824;
}
.gs-cas-input::placeholder {
  color: #A0AEC0;
}
.gs-cas-input:disabled {
  background: transparent;
  cursor: not-allowed;
}
.gs-cas-clear {
  width: 20px;
  height: 20px;
  margin-left: 6px;
  border: 0;
  border-radius: 50%;
  background: #E8EEF6;
  color: #5B6B82;
  cursor: pointer;
  line-height: 18px;
  font-size: 14px;
}
.gs-cas-clear:hover {
  background: #D6E5FE;
  color: #0165FF;
}
.gs-cas-arrow {
  width: 0;
  height: 0;
  margin-left: 8px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid #8A97AB;
}
.gs-cas.is-open .gs-cas-arrow {
  transform: rotate(180deg);
  border-top-color: #0165FF;
}
.gs-cas-panel {
  position: fixed;
  z-index: 4000;
  box-sizing: border-box;
  max-width: calc(100vw - 16px);
  padding: 4px;
  background: #fff;
  border: 1px solid #E4E7ED;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(17, 24, 36, 0.12);
  overflow: hidden;
}
.gs-cas-cols {
  display: flex;
  max-height: 280px;
  overflow: auto;
}
.gs-cas-col {
  flex: 1 1 0;
  min-width: 0;
  max-height: 280px;
  overflow: auto;
  border-right: 1px solid #F0F4FA;
}
.gs-cas-col:last-child {
  border-right: 0;
}
.gs-cas-opt,
.gs-cas-hit {
  display: block;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
  padding: 8px 10px;
  border: 0;
  background: #fff;
  color: #1D2B44;
  cursor: pointer;
  font-size: 13px;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.gs-cas-opt:hover,
.gs-cas-hit:hover,
.gs-cas-opt.is-active {
  background: #F4F8FF;
  color: #0165FF;
}
.gs-cas-hits {
  max-height: 280px;
  overflow: auto;
}
.gs-cas-empty {
  padding: 16px 8px;
  color: #8A97AB;
  font-size: 13px;
  text-align: center;
}
.gs-cas-hint {
  padding: 6px 10px 8px;
  border-top: 1px solid #F0F4FA;
  color: #0165FF;
  font-size: 12px;
  text-align: center;
}
</style>
